import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { LAKSHMIKANT_DASHBOARD_EMAIL } from '../../constants/adminUsers';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import SecondaryBalanceEnquiry from '../PartnerDashboard/SecondaryBalanceEnquiry';
import '../AdminDashboard/BalanceEnquiry.css';
import './LakshmikantDashboard.css';

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₹ ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const DataTable = ({ title, columns, rows, emptyMessage = 'No records for selected date range.' }) => (
  <div className="lakshmi-table-section">
    <h4 className="lakshmi-table-title">{title}</h4>
    {rows.length === 0 ? (
      <p className="lakshmi-empty">{emptyMessage}</p>
    ) : (
      <div className="lakshmi-table-wrap">
        <table className="lakshmi-data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={col.align === 'right' ? 'right' : ''}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${title}-${index}`}>
                {columns.map((col) => {
                  const raw = row[col.key];
                  let display = raw ?? '—';
                  if (col.type === 'currency') display = formatCurrency(raw);
                  else if (col.type === 'date') display = formatDate(raw);
                  return (
                    <td key={col.key} className={col.align === 'right' ? 'right' : ''}>
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const LakshmikantDashboard = () => {
  const [dateFrom, setDateFrom] = useState(getTodayYYYYMMDD());
  const [dateTo, setDateTo] = useState(getTodayYYYYMMDD());
  const [appliedFrom, setAppliedFrom] = useState(getTodayYYYYMMDD());
  const [appliedTo, setAppliedTo] = useState(getTodayYYYYMMDD());
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const adminEmail = LAKSHMIKANT_DASHBOARD_EMAIL;

  const fetchDashboardData = useCallback(async (from, to) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        date_from: from,
        date_to: to,
        admin_email: adminEmail,
      });
      const response = await fetch(`${API_BASE_URL}/api/admin/lakshmikant-dashboard?${params}`, {
        headers: { 'x-admin-email': adminEmail },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to load dashboard data.');
      }
      setDashboardData(data);
    } catch (err) {
      console.error('Lakshmikant dashboard fetch failed:', err);
      setError(err.message || 'Unable to load dashboard data.');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  useEffect(() => {
    fetchDashboardData(appliedFrom, appliedTo);
  }, [appliedFrom, appliedTo, fetchDashboardData]);

  const handleApplyFilter = () => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      alert('From date cannot be greater than To date.');
      return;
    }
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  const handleClearFilter = () => {
    const today = getTodayYYYYMMDD();
    setDateFrom(today);
    setDateTo(today);
    setAppliedFrom(today);
    setAppliedTo(today);
  };

  const cd = dashboardData?.cdList || {};
  const gl = dashboardData?.glList || {};
  const tbd = dashboardData?.tbdList || {};
  const rd = dashboardData?.rdList || {};
  const pigmy = dashboardData?.pigmyList || {};
  const expenditure = dashboardData?.expenditureList || {};

  return (
    <div className="lakshmi-dashboard">
      <h2 className="lakshmi-dashboard-title">Daily Overview Dashboard</h2>

      <div className="filter-section">
        <h3>Date Duration</h3>
        <div className="date-filters">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button type="button" className="apply-button" onClick={handleApplyFilter}>
              Apply Filter
            </button>
            <button type="button" className="clear-button" onClick={handleClearFilter}>
              Reset to Today
            </button>
          </div>
        </div>
        <p className="filter-note">
          Showing data between {appliedFrom} and {appliedTo}
        </p>
      </div>

      <section className="lakshmi-secondary-section">
        <SecondaryBalanceEnquiry
          dateFrom={appliedFrom}
          dateTo={appliedTo}
          hideFilters
          title="Secondary Balance"
        />
      </section>

      {loading ? (
        <div className="lakshmi-loading">Loading transaction lists…</div>
      ) : error ? (
        <div className="lakshmi-error">{error}</div>
      ) : (
        <>
          <section className="lakshmi-list-group">
            <h3 className="lakshmi-group-title">CD List</h3>
            <DataTable
              title="CD Commission Interest"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'commission_interest_amount', label: 'Commission Interest', type: 'currency', align: 'right' },
                { key: 'created_at', label: 'Created At', type: 'date' },
              ]}
              rows={cd.cdCommissionInterest || []}
            />
            <DataTable
              title="CD EMI Payments"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'amount', label: 'Amount', type: 'currency', align: 'right' },
                { key: 'payment_date', label: 'Payment Date', type: 'date' },
              ]}
              rows={cd.cdEmiPayments || []}
            />
            <DataTable
              title="CD Loan"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'loan_amount', label: 'Loan Amount', type: 'currency', align: 'right' },
                { key: 'date', label: 'Date', type: 'date' },
              ]}
              rows={cd.cdLoan || []}
            />
            <DataTable
              title="CD Overdue"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'overdue_paid', label: 'Overdue Paid', type: 'currency', align: 'right' },
                { key: 'md', label: 'MD', type: 'currency', align: 'right' },
                { key: 'payment_date', label: 'Payment Date', type: 'date' },
              ]}
              rows={cd.cdOverdue || []}
            />
          </section>

          <section className="lakshmi-list-group">
            <h3 className="lakshmi-group-title">GL List</h3>
            <DataTable
              title="EMI Payments"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'payment_date', label: 'Payment Date', type: 'date' },
                { key: 'amount', label: 'Amount', type: 'currency', align: 'right' },
              ]}
              rows={gl.emiPayments || []}
            />
            <DataTable
              title="General Loan"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'date', label: 'Date', type: 'date' },
                { key: 'loan_amount', label: 'Loan Amount', type: 'currency', align: 'right' },
                { key: 'commission_amount', label: 'Commission Amount', type: 'currency', align: 'right' },
              ]}
              rows={gl.generalLoan || []}
            />
            <DataTable
              title="Overdue Collected"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'overdue_paid', label: 'Overdue Paid', type: 'currency', align: 'right' },
                { key: 'md', label: 'MD', type: 'currency', align: 'right' },
                { key: 'payment_date', label: 'Payment Date', type: 'date' },
              ]}
              rows={gl.overdueCollected || []}
            />
          </section>

          <section className="lakshmi-list-group">
            <h3 className="lakshmi-group-title">TBD List</h3>
            <DataTable
              title="TBD Daily Payments"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'daily_amount', label: 'Daily Amount', type: 'currency', align: 'right' },
                { key: 'created_at', label: 'Created At', type: 'date' },
              ]}
              rows={tbd.tbdDailyPayments || []}
            />
            <DataTable
              title="TBD Loan"
              columns={[
                { key: 'account_no', label: 'Account No' },
                { key: 'loan_amount', label: 'Loan Amount', type: 'currency', align: 'right' },
                { key: 'commission_amount', label: 'Commission Amount', type: 'currency', align: 'right' },
                { key: 'date', label: 'Date', type: 'date' },
              ]}
              rows={tbd.tbdLoan || []}
            />
          </section>

          <section className="lakshmi-list-group">
            <h3 className="lakshmi-group-title">RD List</h3>
            <DataTable
              title="FD Winner"
              columns={[
                { key: 'registration_no', label: 'Registration No' },
                { key: 'created_at', label: 'Created At', type: 'date' },
              ]}
              rows={rd.fdWinners || []}
            />
            <DataTable
              title="FD Transactions"
              columns={[
                { key: 'registration_no', label: 'Registration No' },
                { key: 'amount', label: 'Amount', type: 'currency', align: 'right' },
                { key: 'created_at', label: 'Created At', type: 'date' },
              ]}
              rows={rd.fdTransactions || []}
            />
          </section>

          <section className="lakshmi-list-group">
            <h3 className="lakshmi-group-title">Pigmy List</h3>
            <DataTable
              title="Pigmy Payments"
              columns={[
                { key: 'registration_no', label: 'Registration No' },
                { key: 'payment_amount', label: 'Payment Amount', type: 'currency', align: 'right' },
                { key: 'created_at', label: 'Created At', type: 'date' },
              ]}
              rows={pigmy.pigmyPayments || []}
            />
            <DataTable
              title="Pigmy Withdrawals"
              columns={[
                { key: 'registration_no', label: 'Registration No' },
                { key: 'withdrawal_amount', label: 'Withdrawal Amount', type: 'currency', align: 'right' },
                { key: 'withdrawal_date', label: 'Withdrawal Date', type: 'date' },
              ]}
              rows={pigmy.pigmyWithdrawals || []}
            />
          </section>

          <section className="lakshmi-list-group">
            <h3 className="lakshmi-group-title">Expenditure List</h3>
            <DataTable
              title="Expense Manager"
              columns={[
                { key: 'headerName', label: 'Header Name' },
                { key: 'amount', label: 'Amount', type: 'currency', align: 'right' },
                { key: 'created_at', label: 'Created At', type: 'date' },
              ]}
              rows={expenditure.expenseManager || []}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default LakshmikantDashboard;
